import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { useRegion } from '../../hooks/useRegion';
import { Card, CardContent } from '../ui/card';

export function BestKitCard({ onCreateKit }) {
  const navigate = useNavigate();
  const { equipmentKits, equipment } = useAppStore();
  const { formatCurrency } = useRegion();

  // Find best performing kit by ROI (or income if no purchase price)
  const bestKit = useMemo(() => {
    if (!equipmentKits || equipmentKits.length === 0) return null;

    return equipmentKits
      .map(kit => {
        const purchasePrice = kit.total_purchase_price || 0;
        const totalIncome = kit.total_income || 0;
        const roi = purchasePrice > 0 
          ? ((totalIncome - purchasePrice) / purchasePrice) * 100 
          : 0;
        return { ...kit, roi };
      })
      .filter(kit => kit.total_income > 0)
      .sort((a, b) => b.roi - a.roi)[0];
  }, [equipmentKits]);

  const header = <p className="eyebrow">best kit</p>;

  // No kits exist - show message
  if (!equipmentKits || equipmentKits.length === 0) {
    return (
      <Card className="rounded-tile bg-surface border-0 h-full">
        <CardContent className="p-6">
          <div className="mb-3">{header}</div>
          <p className="text-sm text-muted-foreground text-left">
            Create your first kit to bundle equipment and track combined performance!
          </p>
        </CardContent>
      </Card>
    );
  }

  // Has kits but none have income yet
  if (!bestKit) {
    return (
      <Card className="rounded-tile bg-surface border-0 h-full">
        <CardContent className="p-6">
          <div className="mb-3">{header}</div>
          <p className="text-sm text-foreground">
            Start renting your kits to see which performs best!
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            You have {equipmentKits.length} kit{equipmentKits.length > 1 ? 's' : ''} ready to go
          </p>
        </CardContent>
      </Card>
    );
  }

  // Jobs the kit's gear went out on; manual history entries have no job
  const jobCount = new Set((bestKit.usage_history || []).map((u) => u.job_id).filter(Boolean)).size;
  const itemNames = (bestKit.equipment_items || [])
    .map((ki) => equipment.find((e) => e.id === ki.equipment_id)?.name)
    .filter(Boolean);

  const Row = ({ label, children, wrap = false }) => (
    <div className={`flex ${wrap ? 'items-start' : 'justify-between'} gap-3 py-2.5 border-t border-border text-[15px]`}>
      <span className="shrink-0">{label}</span>
      <span className={wrap ? 'min-w-0 line-clamp-2' : 'tabular-nums text-right'}>{children}</span>
    </div>
  );

  return (
    <Card
      className="rounded-tile bg-surface border-0 h-full flex flex-col cursor-pointer transition-colors hover:bg-mist"
      onClick={() => navigate('/equipment?tab=kits')}
    >
      <CardContent className="p-6 flex-1 flex flex-col gap-3">
        <p className="eyebrow">best kit</p>
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 className="section-title line-clamp-2 min-w-0">{bestKit.name}</h3>
          {bestKit.total_daily_rate > 0 && (
            <span className="shrink-0 text-sm text-muted-foreground">{formatCurrency(bestKit.total_daily_rate)} a day</span>
          )}
        </div>
        <div className="flex flex-col">
          {jobCount > 0
            ? <Row label="Booked on">{jobCount} {jobCount === 1 ? 'job' : 'jobs'}</Row>
            : <Row label="Rental days">{bestKit.total_days || 0}</Row>}
          <Row label="Income">{formatCurrency(bestKit.total_income || 0)}</Row>
          {itemNames.length > 0 && <Row label="Items" wrap>{itemNames.join(', ')}</Row>}
        </div>
      </CardContent>
    </Card>
  );
}
